// Field Executive workspace — data (client, 2026-10-07). Server: src/api-field
// (the executive's own dashboard / requests) and src/api-invoice-requests
// field-approval routes (Super Admin / Admin / Department Administrator).
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';

export interface FieldVisit { id: string; contactId: string | null; name: string; person: string; location: string; plannedAt: string; status: 'planned' | 'checked_in' | 'completed' | 'cancelled'; checkInAt: string | null; checkOutAt: string | null; requirements: string }
export interface FieldFollowup { id: string; date: string; note: string; category?: string; parentType?: string; parentId?: string | null }
export interface FieldRequestRow {
  id: string; number: string; clientName: string; total: number; status: 'requested' | 'approved' | 'sent' | 'rejected';
  requestedAt: string; rejectReason?: string; invoiceNumber?: string; canDownload?: boolean;
  items?: Array<{ name: string; quantity: number; rate: number; packSize?: string; taxRate?: number }>;
  notes?: string; poAttachments?: Array<{ url: string; name: string }>; requestedByName?: string; reviewedByName?: string; reviewNote?: string;
  /** Credit approval (2026-11-04): over the credit limit or an overdue bill — posting waits for Admin / Super Admin. */
  creditApproval?: CreditApproval | null;
  invoiceId?: string | null;
}
export interface CreditApproval {
  status: 'pending' | 'approved' | 'rejected';
  reasons: Array<'over_limit' | 'overdue'>; message: string;
  limit: number | null; creditDays?: number; outstanding: number; invoiceOutstanding?: number; oldDues?: number;
  amount: number; projected: number; overdueAmount: number; overdueCount: number;
  oldestOverdue?: { number: string; dueDate: string; days: number } | null;
  decidedByName?: string; decidedAt?: string; note?: string;
}
export interface FieldDashboard {
  today: string; user: { name: string };
  visitsToday: FieldVisit[];
  followups: { today: FieldFollowup[]; overdue: FieldFollowup[] };
  month: { key: string; sales: number; invoices: number; target: number; achievementPct: number | null; visitsCompleted: number };
  counts: { contacts: number; openLeads: number; requests: Record<string, number> };
  recentRequests: FieldRequestRow[];
}

export const useFieldDashboard = () => useQuery({
  queryKey: ['field', 'dashboard'],
  queryFn: async () => (await api.get<FieldDashboard>('/api/field/dashboard')).data,
  refetchInterval: 120_000,
});

export const useFieldRequests = (status = '') => useQuery({
  queryKey: ['field', 'requests', status],
  queryFn: async () => (await api.get<{ items: FieldRequestRow[] }>(`/api/field/invoice-requests${status ? `?status=${status}` : ''}`)).data.items,
});

export interface RaiseRequestBody {
  contactId: string; notes?: string; deliveryAddress?: string; paymentTerms?: string;
  items: Array<{ productId?: string; name: string; sku?: string; packSize?: string; hsnSac?: string; quantity: number; rate?: number | ''; taxRate?: number }>;
  poAttachments?: Array<{ url: string; name: string }>;
}
export function useRaiseRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: RaiseRequestBody) => (await api.post<{ id: string; number: string }>('/api/field/invoice-requests', body)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['field'] }),
  });
}

export async function searchProducts(q: string) {
  if (q.trim().length < 2) return [];
  return (await api.get<{ items: Array<{ id: string; name: string; sku: string; packSize: string; hsnSac: string }> }>(`/api/field/products?search=${encodeURIComponent(q)}`)).data.items;
}

/** The executive's own + assigned contacts (the server scopes /api/contacts). */
export async function searchMyContacts(q: string) {
  const p = new URLSearchParams({ limit: '15', compact: 'true', stats: 'total' });
  if (q.trim()) p.set('search', q.trim());
  return (await api.get<{ contacts: Array<{ _id: string; name: string; company?: string; city?: string; phone?: string }> }>(`/api/contacts?${p}`)).data.contacts;
}

export async function uploadFile(file: File) {
  const form = new FormData();
  form.append('file', file);
  form.append('category', 'general');
  const { data } = await api.post<{ fileUrl: string; originalName: string }>('/api/files/upload', form);
  return { url: data.fileUrl, name: data.originalName };
}

export const invoiceDownloadUrl = (requestId: string, view = false) => `/api/field/invoice-requests/${requestId}/invoice${view ? '?view=1' : ''}`;

// ─── Approvers ──────────────────────────────────────────────────────────────
export const useFieldApprovals = (status = 'requested') => useQuery({
  queryKey: ['field', 'approvals', status],
  queryFn: async () => (await api.get<{ items: FieldRequestRow[]; pending: number }>(`/api/invoice-requests/field-approvals?status=${status}`)).data,
});
// ─── Credit approvals — Admin / Super Admin (2026-11-04) ─────────────────────
export const useCreditApprovals = (status = 'pending', enabled = true) => useQuery({
  queryKey: ['field', 'credit-approvals', status],
  queryFn: async () => (await api.get<{ items: FieldRequestRow[]; pending: number }>(`/api/invoice-requests/credit-approvals?status=${status}`)).data,
  enabled,
});
export function useCreditDecision() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { id: string; decision: 'approve' | 'reject'; note?: string }) => (await api.post(`/api/invoice-requests/${v.id}/credit-approval`, v)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['field'] }); qc.invalidateQueries({ queryKey: ['invoices'] }); },
  });
}

export function useApprovalActions() {
  const qc = useQueryClient();
  const done = () => qc.invalidateQueries({ queryKey: ['field'] });
  return {
    approve: useMutation({ mutationFn: async (v: { id: string; items?: Array<{ rate: number; quantity: number }>; note?: string }) => (await api.post(`/api/invoice-requests/${v.id}/approve`, v)).data, onSuccess: done }),
    reject: useMutation({ mutationFn: async (v: { id: string; reason: string }) => (await api.post(`/api/invoice-requests/${v.id}/reject`, v)).data, onSuccess: done }),
  };
}

export const STATUS: Record<FieldRequestRow['status'], { label: string; cls: string }> = {
  requested: { label: 'Waiting for approval', cls: 'bg-amber-50 text-amber-700 ring-amber-200' },
  approved: { label: 'Approved — invoice being issued', cls: 'bg-blue-50 text-blue-700 ring-blue-200' },
  sent: { label: 'Invoice issued', cls: 'bg-emerald-50 text-emerald-700 ring-emerald-200' },
  rejected: { label: 'Rejected', cls: 'bg-rose-50 text-rose-700 ring-rose-200' },
};

export const inr = (n?: number | null) => `₹${Math.round(Number(n) || 0).toLocaleString('en-IN')}`;
export const errMsg = (e: unknown, f: string) => (e as { response?: { data?: { error?: string } } })?.response?.data?.error || f;
